import { createApi } from '@reduxjs/toolkit/query/react';
import { baseUrl, getConfig, fetchLandlordIds, getUserIdFromToken } from '../../../../../serverUrl';
import { baseQueryWithAuth } from '../../baseQueryWithAuth.ts';
import { handleUnauthorized } from '../../auth401.ts';

// Fetch landlord ID asynchronously before making API requests
const getLandlordId = async () => {
    try {
        const landlordId = await fetchLandlordIds();
        return landlordId;
    } catch (error) {
        console.error('Error fetching Landlord ID:', error);
        return null;
    }
};

// Get authorization config
const getConfigData = () => getConfig();

type OutboxQuery = {
    page?: number;
    pageSize?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
};

export const communicationSlice = createApi({
    reducerPath: 'CommunicationApi',
    baseQuery: baseQueryWithAuth,
    tagTypes: ['Communication'],

    endpoints: (builder) => ({
        addMessageTemplate: builder.mutation({
            query: (data) => ({
                url: '/communication/message-templates/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Communication'],
        }),
        updateMessageTemplate: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `communication/message-templates/${id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Communication'],
        }),
        deleteMessageTemplate: builder.mutation({
            query: (id) => ({
                url: `communication/message-templates/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Communication'],
        }),
        getMessaeTemplate: builder.query({
            query: () => {
                const userId = getUserIdFromToken();
                return `/communication/message-templates/?created_by_message_template=${userId}`;
            },
            providesTags: ['Communication'],
        }),
        sendReminder: builder.mutation({
            // Accepts an object: { data, params }
            query: ({ data, params }) => {
                // Build query string from params object
                let queryString = '';
                if (params && typeof params === 'object') {
                    const esc = encodeURIComponent;
                    queryString =
                        '?' +
                        Object.keys(params)
                            .map(k => esc(k) + '=' + esc(params[k]))
                            .join('&');
                }
                return {
                    url: `/landlord/send_payment_reminder/${queryString}`,
                    method: 'POST',
                    body: data,
                };
            },
            invalidatesTags: ['Communication'],
        }),

        // get the communication credits
        getCommunicationCredits: builder.query<void, void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const url = `${baseUrl}/communication/get-sms-balance/?landlord_id=${landlordId}`;

                    const response = await fetch(url, getConfigData());
                    if (response.status === 401) {
                        handleUnauthorized();
                        return { error: { status: 401, data: 'Unauthorized' } };
                    }
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Communication'],
        }),

        addSMSCredits: builder.mutation({
            queryFn: async (data: any, _api, _extraOptions, baseQuery) => {
                try {
                    const landlordId = await getLandlordId();
                    const userId = getUserIdFromToken();
                    if (!landlordId) {
                        return { error: { status: 400, data: "Landlord ID not found" } };
                    }
                    if (!userId) {
                        return { error: { status: 400, data: "User ID not found" } };
                    }

                    const formData = new FormData();
                    formData.append('landlord_id', landlordId);
                    formData.append('user_id', userId);
                    formData.append('source', 'web');

                    // Append additional data fields
                    for (const key in data) {
                        formData.append(key, data[key]);
                    }

                    return baseQuery({
                        url: '/communication/topup-credit/',
                        method: 'POST',
                        body: formData,
                    });
                } catch (error) {
                    return { error: { status: 500, data: "Internal server error" } };
                }
            },
            invalidatesTags: ['Communication'],
        }),

        getLanlordTenantSta: builder.query<void, void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const url = `${baseUrl}/landlord/landlord-communication-stats-view/?landlord_id=${landlordId}`;

                    const response = await fetch(url, getConfigData());
                    if (response.status === 401) {
                        handleUnauthorized();
                        return { error: { status: 401, data: 'Unauthorized' } };
                    }
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Communication'],
        }),
        sendMessages: builder.mutation({
            query: ({ message, phone_numbers }) => ({
                url: '/communication/send-landlord-messages/',
                method: 'POST',
                body: { message, phone_numbers }
            }),
        }),
        getLndlordOutboxMessages: builder.query<any, OutboxQuery | undefined>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const params = new URLSearchParams({
                        landlord_id: String(landlordId),
                        page: String(args?.page ?? 1),
                        page_size: String(args?.pageSize ?? 20),
                    });
                    if (args?.search?.trim()) params.set('search', args.search.trim());
                    if (args?.startDate) params.set('start_date', args.startDate);
                    if (args?.endDate) params.set('end_date', args.endDate);
                    const url = `${baseUrl}/communication/get-landlord-messages/?${params.toString()}`;

                    const response = await fetch(url, getConfigData());
                    if (response.status === 401) {
                        handleUnauthorized();
                        return { error: { status: 401, data: 'Unauthorized' } };
                    }
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Communication'],
        }),

        getCommunicationCreditTranactions: builder.query<void, void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const url = `${baseUrl}/communication/sms_credits_transactions?landlord_id=${landlordId}`;

                    const response = await fetch(url, getConfigData());
                    if (response.status === 401) {
                        handleUnauthorized();
                        return { error: { status: 401, data: 'Unauthorized' } };
                    }
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Communication'],
        }),
    }),
});

// Export hooks for usage in components
export const { useAddMessageTemplateMutation, useUpdateMessageTemplateMutation, useDeleteMessageTemplateMutation, useGetMessaeTemplateQuery, useSendReminderMutation, useGetCommunicationCreditsQuery, useAddSMSCreditsMutation, useGetLanlordTenantStaQuery, useSendMessagesMutation,
    useGetLndlordOutboxMessagesQuery, useGetCommunicationCreditTranactionsQuery } = communicationSlice;
